import React from "react";
import { Box, Heading, Text, VStack, Icon, SimpleGrid } from "@chakra-ui/react";
import { Link } from "react-router-dom";
import { FaTools, FaWrench, FaPaintRoller, FaLightbulb } from "react-icons/fa";

const TricksOfTheTradePage = () => {
  return (
    <Box minH="100vh" bg="white">
      {/* Hero Section */}
      <Box
        bg="white"
        py={{ base: 16, md: 24 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Heading
          as="h1"
          fontSize={{ base: "4xl", md: "6xl" }}
          fontWeight="800"
          color="gray.900"
          mb={6}
          fontFamily="'Work Sans', sans-serif"
        >
          Tricks of the Trade
        </Heading>
        <Text
          fontSize={{ base: "lg", md: "xl" }}
          color="gray.600"
          maxW="2xl"
          mx="auto"
        >
          Whether you're a DIY enthusiast or a professional, these handy tips
          and tricks will help you take your home renovation and maintenance
          skills to the next level.
        </Text>
      </Box>

      {/* Tips Cards Section */}
      <Box bg="gray.50" py={{ base: 12, md: 20 }} px={{ base: 6, md: 12 }}>
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={8}
          maxW="1200px"
          mx="auto"
        >
          {/* Tool Maintenance Tip */}
          <Link to="/tool-maintenance" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaTools} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Tool Maintenance
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Keep your tools in top shape by regularly cleaning, oiling,
                  and storing them properly. Well-maintained tools last longer
                  and make every job easier.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* Quick Fixes Tip */}
          <Link to="/quick-fix" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaWrench} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Quick Fixes
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Learn easy fixes for common problems like leaky faucets,
                  broken tiles, and creaky doors. These tricks can save you time
                  and money by avoiding expensive repairs.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* DIY Painting Hacks */}
          <Link to="/diy-painting" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaPaintRoller} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  DIY Painting Hacks
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Get that professional look by using painter's tape, the right
                  brush techniques, and understanding how to mix colors for a
                  flawless finish.
                </Text>
              </VStack>
            </Box>
          </Link>

          {/* Lighting & Electrical Tips */}
          <Link to="/electric-tips" style={{ textDecoration: "none" }}>
            <Box
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              p={8}
              boxShadow="sm"
              transition="all 0.3s ease"
              _hover={{
                boxShadow: "xl",
                borderColor: "orange.400",
                transform: "translateY(-4px)",
              }}
              cursor="pointer"
            >
              <VStack align="start" spacing={4}>
                <Box
                  bg="orange.50"
                  p={4}
                  borderRadius="full"
                  display="inline-flex"
                >
                  <Icon as={FaLightbulb} w={8} h={8} color="orange.500" />
                </Box>
                <Heading fontSize="2xl" fontWeight="700" color="gray.900">
                  Lighting & Electrical Tips
                </Heading>
                <Text color="gray.600" fontSize="md">
                  Save on electricity bills by using LED bulbs and smart plugs.
                  Learn the basics of safely handling electrical outlets,
                  switches, and wiring.
                </Text>
              </VStack>
            </Box>
          </Link>
        </SimpleGrid>
      </Box>

      {/* Final Message Section */}
      <Box
        bg="white"
        py={{ base: 12, md: 16 }}
        px={{ base: 6, md: 12 }}
        textAlign="center"
      >
        <Text
          fontSize={{ base: "xl", md: "2xl" }}
          fontWeight="600"
          color="gray.800"
          maxW="3xl"
          mx="auto"
        >
          With these tips, we hope to empower you to take on your next project
          with confidence. Let's make home improvement easier, one trick at a
          time!
        </Text>
      </Box>
    </Box>
  );
};

export default TricksOfTheTradePage;
